<template>
  <MainLayout>
    <div
      class="parallax !z-40 max-md:hidden"
      data-parallax
      data-parallax-speed="0.25"
      data-parallax-direction="down">
      <div class="relative w-screen">
        <img
          class="absolute left-0 lg:left-8 top-[-2vh] lg:top-[24vh] w-[20vw]"
          src="/images/bg-visual-left-top.webp"
          alt="" />
        <img
          class="absolute -left-[10vw] top-[72vh] w-[20vw]"
          src="/images/bg-visual-left-bottom.webp"
          alt="" />
        <img
          class="absolute -right-[20vw] lg:-right-[6vw] top-[0vh] lg:top-[40vh] w-[30vw] opacity-90"
          src="/images/bg-visual-right-top.webp"
          alt="" />
      </div>
    </div>

    <section id="hero" class="flex flex-col !gap-y-22 backgroundgradient backgroundgradient-top">
      <div class="flex flex-col gap-y-9 items-center">
        <span
          class="btn bg-[#F2F6F7] flex items-center gap-x-2 text-sm font-instrumentSans pointer-events-none">
          <span>🍔</span>
          <span class="font-normal">
            <span class="font-semibold">{{ t('pages.home.hero.pill.label') }}</span>
            <i class="bi bi-dot"></i>
            <span>{{ t('pages.home.hero.pill.text') }}</span>
          </span>
          <!--<i class="bi bi-arrow-right mt-[1px] font-bold"></i>-->
        </span>

        <div class="flex flex-col gap-y-4.5 lg:gap-y-8">
          <h1
            class="text-[29px] leading-[32px] lg:text-[64px] lg:leading-[70px] font-bold text-dark text-center font-markPro lg:max-w-[16ch] tracking-[-0.1rem] max-md:-mx-[1rem]">
            {{ t('pages.home.hero.heading.title') }}
            <RotatingWord :words="heroWords" />
          </h1>

          <div class="flex flex-col text-center leading-[27px] lg:text-lg font-semibold">
            <p class="text-lighter">{{ t('pages.home.hero.saving_time') }}</p>
            <p class="text-dark">
              {{ t('pages.home.hero.boosting_business') }}
            </p>
          </div>
        </div>

        <div class="flex gap-x-3 gap-y-4 items-center flex-row">
          <a
            v-track-event="'onboarding_start'"
            href="https://merchant.bistroo.nl/register"
            class="btn btn-lg btn-primary whitespace-nowrap">
            {{ t('main.actions.create_your_shop') }}
          </a>
          <a
            href="https://webforms.pipedrive.com/f/6k1fJdz7xgA49xFsdiLKDuoJ6y0vj7QgYFKhNOo8Gf4SEA7lu5dJ0YDBJRJ67NCUn1"
            target="_blank"
            class="btn btn-lg btn-dark whitespace-nowrap"
            >{{ t('main.actions.product_demo') }}
          </a>
        </div>
      </div>

      <img
        loading="eager"
        fetchpriority="high"
        class="max-w-full"
        height="611"
        width="857"
        src="/images/img-header-mockup.webp"
        :alt="t('pages.home.hero.header_mockup_alt')" />
      <img
        loading="eager"
        fetchpriority="high"
        class="max-w-[94%] object-bottom opacity-60 lg:pl-6"
        src="/images/img-logos-greyscale.webp"
        :alt="t('pages.home.hero.partners_alt')" />
    </section>

    <section id="landing-pages">
      <div class="flex flex-col gap-y-6 items-center">
        <h2
          class="text-[26px] leading-[36px] lg:text-5xl lg:leading-[56px] font-bold text-dark text-center font-markPro max-w-[850px] tracking-[-0.06rem] lg:tracking-[-0.08rem]">
          {{ t('pages.home.landing_pages.heading.create') }}
          <span class="lg:hidden whitespace-nowrap">{{
            t('pages.home.landing_pages.heading.your_restaurant')
          }}</span>
          <span class="max-lg:hidden text-gradient-primary whitespace-nowrap">{{
            t('pages.home.landing_pages.heading.your_restaurant')
          }}</span>

          {{ t('pages.home.landing_pages.heading.shop_in_minutes') }}
        </h2>

        <div class="flex flex-col gap-y-4">
          <div class="flex flex-col text-center lg:text-lg font-semibold">
            <p class="text-lighter">
              {{ t('pages.home.landing_pages.personalized_webshop') }}
            </p>
            <p class="text-dark">
              {{ t('pages.home.landing_pages.start_free') }}
            </p>
          </div>
        </div>

        <a
          v-track-event="'onboarding_start'"
          href="https://merchant.bistroo.nl/register"
          class="btn btn-lg btn-primary whitespace-nowrap mt-3">
          {{ t('pages.home.landing_pages.actions.create_your_website') }}
        </a>
      </div>

      <ScrollImageSlider :rows="merchantPageImageRows" />
    </section>

    <section id="key-features">
      <div class="flex flex-col gap-y-6 lg:gap-y-10 items-center">
        <a
          href="#"
          class="w-fit px-4 py-2 max-md:mb-3 rounded-full bg-white drop-shadow-md flex items-center gap-x-2 text-sm font-instrumentSans">
          <span>🎯</span>
          <span class="font-normal"
            ><span class="font-semibold">{{ t('pages.home.key_features.pill.label') }}</span>
            <i class="bi bi-dot"></i>
            <span>{{ t('pages.home.key_features.pill.text') }}</span>
          </span>
        </a>

        <h2
          class="text-[26px] leading-[36px] lg:text-5xl lg:leading-[56px] font-bold text-dark text-center font-markPro max-w-[700px] tracking-[-0.06rem] lg:tracking-[-0.08rem]">
          {{ t('pages.home.key_features.heading') }}
        </h2>

        <div class="flex flex-col gap-y-4">
          <div class="flex flex-col text-center lg:text-lg font-semibold">
            <p class="text-lighter">
              {{ t('pages.home.key_features.personalize_order_experience') }}
            </p>
            <p class="text-dark">
              {{ t('pages.home.key_features.boost_sales') }}
            </p>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-12 gap-8 max-w-[1200px]">
        <div class="md:col-span-8 row-span-3 bg-white rounded-lg drop-shadow max-md:order-1">
          <div class="flex flex-col justify-between h-[100%]">
            <div class="flex flex-col gap-y-4 p-8 pb-0">
              <h4 class="text-gradient-primary font-markPro text-2xl font-bold tracking-[-0.04rem]">
                {{ t('pages.home.key_features.cards.order_management_with_ease.title') }}
              </h4>

              <p class="text-lighter font-medium max-w-[650px]">
                {{ t('pages.home.key_features.cards.order_management_with_ease.text') }}
              </p>

              <RouterLink :to="{ name: 'products.manager' }" class="btn btn-light mt-4">
                {{ t('pages.home.key_features.cards.order_management_with_ease.button') }}
              </RouterLink>
            </div>

            <div
              class="flex justify-end items-end mt-10 max-md:ml-14 lg:mt-[-8rem] pointer-events-none">
              <img src="/images/img-merchantportal-blocks.webp" alt="Merchant portal" width="450" />
            </div>
          </div>
        </div>

        <div
          class="md:col-span-4 row-span-4 bg-white rounded-lg p-8 drop-shadow flex flex-col gap-y-4 justify-between max-md:order-4">
          <div class="flex flex-col gap-y-4">
            <h4 class="font-markPro text-2xl font-bold tracking-[-0.04rem]">
              {{ t('pages.home.key_features.cards.plug_and_play.title') }}
            </h4>

            <p class="text-lighter">
              {{ t('pages.home.key_features.cards.plug_and_play.text') }}
            </p>
          </div>

          <img
            class="object-cover rounded-xl overflow-hidden aspect-[3/4] max-md:mt-6 max-md:h-80"
            src="/images/img-posdevice-visual-clear.webp"
            alt="POS handheld" />
        </div>

        <div class="md:col-span-4 row-span-2 bg-white rounded-lg drop-shadow max-md:order-2">
          <div class="flex flex-col justify-between h-[100%]">
            <div class="flex flex-col gap-y-4 p-8">
              <h4 class="font-markPro text-2xl font-bold tracking-[-0.04rem]">
                {{ t('pages.home.key_features.cards.peak_times.title') }}
              </h4>
              <p class="text-lighter">
                {{ t('pages.home.key_features.cards.peak_times.text') }}
              </p>
            </div>

            <img
              class="rounded-lg overflow-hidden place-self-end"
              src="/images/img-controlpeak.svg"
              alt="Control over peak times and busy hours" />
          </div>
        </div>

        <div class="md:col-span-4 row-span-2 bg-white rounded-lg drop-shadow max-md:order-3">
          <div class="flex flex-col justify-between h-[100%]">
            <div class="flex flex-col gap-y-4 p-8">
              <h4 class="font-markPro text-gradient-primary text-2xl font-bold tracking-[-0.04rem]">
                {{ t('pages.home.key_features.cards.instant_payments.title') }}
              </h4>
              <p class="text-lighter">
                {{ t('pages.home.key_features.cards.instant_payments.text') }}
              </p>
            </div>

            <img
              class="rounded-lg overflow-hidden place-self-end max-md:pl-15"
              src="/images/img-instantpayments.svg"
              :alt="t('pages.home.key_features.cards.instant_payments.img_alt')" />
          </div>
        </div>

        <div class="md:col-span-4 row-span-1 bg-white rounded-lg p-8 drop-shadow max-md:order-5">
          <div class="flex flex-col gap-y-4">
            <h4 class="font-markPro text-2xl font-bold tracking-[-0.04rem]">
              {{ t('pages.home.key_features.cards.many_more.title') }}
            </h4>
            <p class="text-lighter">
              {{ t('pages.home.key_features.cards.many_more.text') }}
            </p>

            <RouterLink :to="{ name: 'products.manager' }" class="btn btn-light mt-4">
              {{ t('pages.home.key_features.cards.many_more.button') }}
            </RouterLink>
          </div>
        </div>
      </div>
    </section>

    <section id="paragraphs" class="mt-10 !gap-30">
      <div
        class="flex flex-col gap-10 max-lg:items-center max-md:flex-col-reverse max-lg:p-4 lg:grid lg:grid-cols-5">
        <div class="col-span-3 flex flex-col gap-y-9 order-1">
          <span
            class="w-fit px-4 py-2 rounded-full bg-white drop-shadow-md flex items-center gap-x-2 text-sm font-instrumentSans lg:mt-2 lg:ml-[3.8rem]">
            <span>🔥</span>
            <span class="font-normal"
              ><span class="font-semibold">Bistroo Suite</span>
              <i class="bi bi-dot"></i>
              <span>Full service integration</span>
            </span>
          </span>

          <h3
            class="text-3xl lg:text-5xl font-bold text-dark font-markPro lg:max-w-[20ch] lg:pl-[4rem] lg:leading-14 tracking-[-0.1rem]">
            {{ t('pages.home.paragraphs.paragraph_1.title') }}
          </h3>

          <p class="text-lighter lg:max-w-[90%] lg:pl-[4rem] font-semibold">
            {{ t('pages.home.paragraphs.paragraph_1.text') }}
          </p>

          <RouterLink :to="{ name: 'products.pos' }" class="btn btn-lg btn-light text-sm lg:ml-[3.8rem]">
            {{ t('pages.home.paragraphs.paragraph_1.cta') }}
          </RouterLink>
        </div>

        <div class="relative lg:col-span-2">
          <img class="w-[318px]" src="/images/img-home-paragraphs-pos.webp" alt="POS" />
          <img
            class="absolute top-16 -right-8 w-[287px]"
            src="/images/img-home-paragraphs-float-pos.webp"
            alt="POS incoming orders" />
        </div>
      </div>

      <div
        class="flex flex-col gap-10 max-lg:items-center max-md:flex-col-reverse max-lg:p-4 lg:grid lg:grid-cols-6">
        <div class="max-lg:order-2 col-span-4 flex flex-col gap-y-8 lg:pr-40 justify-center">
          <h3
            class="text-3xl lg:text-5xl font-bold text-dark font-markPro lg:max-w-[16ch] lg:leading-14 tracking-[-0.1rem]">
            {{ t('pages.home.paragraphs.paragraph_2.title') }}
          </h3>

          <p class="text-lighter lg:max-w-[95%] font-semibold">
            {{ t('pages.home.paragraphs.paragraph_2.text') }}
          </p>

          <div class="flex">
            <RouterLink :to="{ name: 'products.kiosk' }" class="btn btn-lg btn-light text-sm">
              {{ t('pages.home.paragraphs.paragraph_2.cta') }}
            </RouterLink>
            <RouterLink :to="{ name: 'products.qr' }" class="btn btn-lg btn-light text-sm ml-[1.8rem]">
              {{ t('pages.home.paragraphs.paragraph_2.cta_2') }}
            </RouterLink>
          </div>
        </div>

        <div class="relative col-span-2">
          <img class=" w-[318px]" src="/images/img-home-paragraphs-kiosk.webp" alt="Kiosk" />
          <img
            class="absolute -bottom-31 lg:-bottom-20 -left-28 lg:-left-55"
            src="/images/img-home-paragraphs-float-qr-ordering.webp"
            alt="Qr code ordering" />
        </div>
      </div>

      <div
        class="flex flex-col gap-10 max-lg:items-center max-md:flex-col-reverse max-lg:p-4 lg:grid lg:grid-cols-5 lg:pt-10">
        <div class="col-span-3 flex flex-col gap-y-8 order-1 lg:ml-[4rem] mt-18 lg:mt-5">
          <span
            class="w-fit px-4 py-2 rounded-full bg-gradient-primary text-white drop-shadow-md flex items-center gap-x-2 text-sm font-instrumentSans lg:mt-2">
            <span class="font-semibold">DIGITAL FIRST</span>
          </span>

          <h3
            class="text-3xl lg:text-5xl font-bold text-dark font-markPro lg:max-w-[20ch] lg:leading-14 tracking-[-0.1rem]">
            {{ t('pages.home.paragraphs.paragraph_3.title') }}
          </h3>

          <p class="text-lighter lg:max-w-[90%]s font-semibold">
            {{ t('pages.home.paragraphs.paragraph_3.text') }}
          </p>

          <RouterLink :to="{ name: 'products.manager' }" class="btn btn-lg btn-light text-sm">
            {{ t('pages.home.paragraphs.paragraph_3.cta') }}
          </RouterLink>
        </div>

        <div class="relative lg:col-span-2">
          <img src="/images/img-home-paragraphs-manager.webp" alt="Sandwich store" />
          <img
            class="absolute top-28 lg:top-15 left-12 lg:left-9"
            src="/images/img-home-paragraphs-float-manager.webp"
            alt="Manager window" />
        </div>
      </div>
    </section>

    <div ref="productsWrapper">
      <section id="bistroo-products">
        <ProductSlider />
      </section>
    </div>

    <section id="integrations">
      <div
        class="bg-linear-[180deg,#E3EEF7_55%,#F7F9FB] lg:h-[650px] flex justify-center items-center p-4 lg:p-8 rounded-2xl w-full mt-10 lg:mt-20">
        <div
          class="bg-linear-[180deg,rgba(255,255,255,0.5)_55%,#F7F9FB] rounded-2xl w-full h-full flex justify-center items-center px-8 lg:px-32 relative">
          <img
            src="/images/integrations/icon-stripe.svg"
            alt="Stripe"
            class="absolute left-[2%] -top-7 lg:left-[5%] lg:-top-14 w-14 h-14 lg:w-30 lg:h-30" />

          <img
            src="/images/integrations/icon-facebook.png"
            alt="Facebook"
            class="p-2 lg:p-4 absolute left-[25%] -top-12 w-18 lg:left-[29%] lg:-top-24 w-18 h-18 lg:w-36 lg:h-36" />

          <img
            src="/images/integrations/icon-lightspeed.svg"
            alt="Lightspeed"
            class="p-2 lg:p-4 absolute left-[54%] -top-12 lg:left-[58%] lg:-top-22 w-18 h-18 lg:w-38 lg:h-38" />

          <img
            src="/images/integrations/icon-exact.svg"
            alt="Exact Online"
            class="p-2 lg:p-4 absolute left-[82%] -top-10 lg:left-[86%] lg:-top-18 w-18 h-18 lg:w-36 lg:h-36" />

          <img
            src="/images/integrations/icon-slack.svg"
            alt="Slack"
            class="p-2 lg:p-4 absolute left-[17%] top-4 lg:left-[15%] lg:top-8 w-18 h-18 lg:w-40 lg:h-40" />

          <img
            src="/images/integrations/icon-winston.png"
            alt="Winston"
            class="absolute left-[34%] top-18 w-34 h-34 max-lg:hidden" />

          <img
            src="/images/integrations/icon-mollie.svg"
            alt="Mollie"
            class="absolute left-[49%] top-9 lg:left-[55%] lg:top-18 w-12 h-12 lg:w-30 lg:h-30" />

          <img
            src="/images/integrations/icon-mpluskassa.svg"
            alt="mPlusKassa"
            class="absolute left-[72%] top-7 lg:left-[74%] lg:top-15 w-12 h-12 lg:w-30 lg:h-30" />

          <img
            src="/images/integrations/icon-google.png"
            alt="Google"
            class="p-2 lg:p-4 absolute left-[87%] top-11.5 lg:left-[85%] lg:top-33 w-18 h-18 lg:w-42 lg:h-42" />

          <img
            src="/images/integrations/icon-untill.png"
            alt="UnTill"
            class="p-2 lg:p-4 absolute left-[-6%] top-11 lg:left-[-0.5%] lg:top-38 w-18 h-18 lg:w-40 lg:h-40" />

          <div class="flex flex-col gap-y-8 items-center mt-28 lg:mt-46">
            <h1
              class="text-[26px] leading-[36px] lg:text-5xl lg:leading-[56px] font-bold text-dark text-center font-markPro max-w-[750px] tracking-[-0.06rem] lg:tracking-[-0.08rem]">
              {{ t('pages.home.integrations.heading') }}
            </h1>

            <p class="text-lighter text-center font-semibold max-w-[630px] leading-[28px]">
              {{ t('pages.home.integrations.text') }}
            </p>

            <RouterLink
              :to="{ name: 'info.integrations' }"
              class="btn btn-lg btn-primary mt-4 text-sm">
              {{ t('pages.home.integrations.action') }}
            </RouterLink>
          </div>
        </div>
      </div>
    </section>

    <section id="testimonials" class="backgroundgradient backgroundgradient-bottom">
      <div class="flex flex-col gap-y-4 items-center">
        <div class="flex flex-col gap-y-6 lg:gap-y-8 items-center">
          <a
            href="#"
            class="w-fit px-4 py-2 rounded-full bg-white drop-shadow-md flex items-center gap-x-2 text-sm font-instrumentSans mb-4">
            <span>🏆</span>
            <span class="font-normal">
              <span class="font-semibold">{{ t('pages.home.testimonials.pill.label') }}</span>
              <i class="bi bi-dot"></i>
              <span>{{ t('pages.home.testimonials.pill.text') }}</span>
            </span>
          </a>

          <h1
            class="text-[26px] leading-[36px] lg:text-5xl lg:leading-[56px] font-bold text-dark text-center font-markPro max-w-[700px] tracking-[-0.06rem] lg:tracking-[-0.08rem]">
            {{ t('pages.home.testimonials.heading') }}
          </h1>

          <div class="flex flex-col gap-y-4">
            <div class="flex flex-col text-center lg:text-lg font-semibold">
              <p class="text-lighter">
                {{ t('pages.home.testimonials.empowered_our_clients') }}
              </p>
            </div>
          </div>
        </div>

        <div class="grid lg:grid-cols-2 gap-2 lg:gap-12 p-8 lg:ml-[5%] lg:mr-[5%]">
          <img
            class="rounded-[25px] overflow-hidden w-full aspect-square object-cover object-center max-lg:max-w-[320px] max-h-[400px] max-lg:mx-auto"
            src="/images/cases/lunchroom/img-two-b-mondie.webp"
            :alt="t('pages.home.testimonials.testimonials.hero.img_alt')" />

          <div class="flex flex-col gap-y-2 max-md:items-center">
            <h4 class="text-xl max-md:text-center lg:text-3xl font-semibold mt-10 mb-5">
              {{ t('pages.home.testimonials.testimonials.hero.text') }}
            </h4>

            <div class="flex max-md:flex-col items-center gap-x-8">
              <span class="font-semibold">
                {{ t('pages.home.testimonials.testimonials.hero.author') }}
              </span>
              <span class="text-lighter">
                {{ t('pages.home.testimonials.testimonials.hero.company') }}
              </span>
            </div>

            <RouterLink
              :to="{ name: 'cases.lunchroom' }"
              class="btn btn-light mt-4 lg:mt-10 max-md:btn-sm">
              {{ t('pages.home.testimonials.testimonials.hero.action') }}
            </RouterLink>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-16">
          <div class="testimonial">
            <div class="flex items-center gap-x-2">
              <i class="bi bi-star-fill text-primary"></i>
              <i class="bi bi-star-fill text-primary"></i>
              <i class="bi bi-star-fill text-primary"></i>
              <i class="bi bi-star-fill text-primary"></i>
              <i class="bi bi-star-fill text-primary"></i>
            </div>

            <p class="text-center text-sm text-lighter">
              {{ t('pages.home.testimonials.testimonials.reviews.two.text') }}
            </p>

            <div class="flex flex-col gap-y-4 items-center mt-2 lg:mt-5">
              <img src="/images/cases/grillroom/img-grillroom-hamza-small.webp" width="60" />
              <div class="flex flex-col items-center">
                <h4 class="font-semibold">
                  {{ t('pages.home.testimonials.testimonials.reviews.two.author') }}
                </h4>
                <p class="text-lighter text-sm">
                  {{ t('pages.home.testimonials.testimonials.reviews.two.company_role') }}
                </p>
              </div>

              <RouterLink :to="{ name: 'cases.grillroom' }" class="btn btn-light mt-1 lg:mt-4">
                {{ t('pages.home.testimonials.testimonials.reviews.two.action') }}
              </RouterLink>
            </div>
          </div>

          <div class="testimonial">
            <div class="flex items-center gap-x-2">
              <i class="bi bi-star-fill text-primary"></i>
              <i class="bi bi-star-fill text-primary"></i>
              <i class="bi bi-star-fill text-primary"></i>
              <i class="bi bi-star-fill text-primary"></i>
              <i class="bi bi-star-fill text-primary"></i>
            </div>

            <p class="text-center text-sm text-lighter">
              {{ t('pages.home.testimonials.testimonials.reviews.one.text') }}
            </p>

            <div class="flex flex-col gap-y-4 items-center mt-2 lg:mt-5">
              <img
                class="rounded-full"
                src="/images/cases/italian/img-italian-roberta-small.webp"
                width="60" />
              <div class="flex flex-col items-center">
                <h4 class="font-semibold">
                  {{ t('pages.home.testimonials.testimonials.reviews.one.author') }}
                </h4>
                <p class="text-lighter text-sm">
                  {{ t('pages.home.testimonials.testimonials.reviews.one.company_role') }}
                </p>
              </div>

              <RouterLink :to="{ name: 'cases.italian' }" class="btn btn-light mt-1 lg:mt-4">
                {{ t('pages.home.testimonials.testimonials.reviews.one.action') }}
              </RouterLink>
            </div>
          </div>

          <div class="testimonial">
            <div class="flex items-center gap-x-2">
              <i class="bi bi-star-fill text-primary"></i>
              <i class="bi bi-star-fill text-primary"></i>
              <i class="bi bi-star-fill text-primary"></i>
              <i class="bi bi-star-fill text-primary"></i>
              <i class="bi bi-star-fill text-primary"></i>
            </div>

            <p class="text-center text-sm text-lighter">
              {{ t('pages.home.testimonials.testimonials.reviews.three.text') }}
            </p>

            <div class="flex flex-col gap-y-4 items-center mt-2 lg:mt-5">
              <img src="/images/cases/cafetaria/img-cafetaria-henk-small.webp" width="60" />
              <div class="flex flex-col items-center">
                <h4 class="font-semibold">
                  {{ t('pages.home.testimonials.testimonials.reviews.three.author') }}
                </h4>
                <p class="text-lighter text-sm">
                  {{ t('pages.home.testimonials.testimonials.reviews.three.company_role') }}
                </p>
              </div>

              <RouterLink :to="{ name: 'cases.cafetaria' }" class="btn btn-light mt-1 lg:mt-4">
                {{ t('pages.home.testimonials.testimonials.reviews.three.action') }}
              </RouterLink>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="cta" class="backgroundgradient backgroundgradient-fill">
      <div class="w-full rounded-2xl py-16 max-md:px-14 lg:pt-26 lg:pb-20 relative overflow-hidden">
        <div class="absolute inset-0 z-10 bg-[#E3EEF7]"></div>
        <div
          class="parallax"
          data-parallax
          data-parallax-speed="0.05"
          data-parallax-direction="down">
          <div class="relative w-full h-full">
            <img
              class="absolute top-0 left-0 -translate-x-1/3 h-[150%] rotate-[45%]"
              src="/images/bg-visual-left-top.webp"
              alt="" />

            <img
              class="absolute right-0 translate-x-1/3 -top-1/4 lg:-top-1/2 h-1/2 lg:h-full rotate-[-20deg]"
              src="/images/bg-visual-right-top.webp"
              alt="" />
          </div>
        </div>

        <div class="flex flex-col gap-y-5.5 justify-center items-center text-center relative z-30">
          <h4
            class="text-[32px] leading-[42px] lg:text-5xl font-bold text-dark text-center font-markPro block tracking-[-0.08rem]">
            {{ t('main.cta_block.open_your_webshop_today') }}
            <span class="text-gradient-primary whitespace-nowrap">
              {{ t('main.cta_block.its_free') }}
            </span>
          </h4>

          <p class="text-lg lg:text-[24px] text-lighter font-semibold">
            {{ t('main.cta_block.start_generating_more_orders') }}
          </p>

          <a
            v-track-event="'onboarding_start'"
            href="https://merchant.bistroo.nl/register"
            class="btn btn-lg btn-primary flex items-center gap-x-3 mt-4">
            <span>
              {{ t('main.cta_block.get_started_for_free') }}
            </span>
            <i class="bi bi-arrow-right text-lg mt-[3px]"></i>
          </a>
        </div>
      </div>
    </section>
  </MainLayout>
</template>

<script setup lang="ts">
import ScrollImageSlider from '@/components/ScrollImageSlider.vue'
import MainLayout from '@/layouts/MainLayout.vue'
import { useHead } from '@unhead/vue'
import { publicPath } from '@/utils/publicPath.ts'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import ProductSlider from './components/ProductSlider.vue'
import RotatingWord from './components/RotatingWord.vue'
const { t, tm } = useI18n()

const heroWords = computed(() => tm('pages.home.hero.heading.words') as string[])

useHead(computed(() => ({
  title: t('pages.home.seo.title'),
  meta: [{ name: 'description', content: t('pages.home.seo.description') }],
})))

const merchantPageImageRows = [
  [
    publicPath('/images/merchantpage/template-1.webp'),
    publicPath('/images/merchantpage/template-2.webp'),
    publicPath('/images/merchantpage/template-3.webp'),
    publicPath('/images/merchantpage/template-4.webp'),
    publicPath('/images/merchantpage/template-5.webp'),
    publicPath('/images/merchantpage/template-7.webp'),
  ],
  [
    publicPath('/images/merchantpage/template-6.webp'),
    publicPath('/images/merchantpage/template-7.webp'),
    publicPath('/images/merchantpage/template-8.webp'),
    publicPath('/images/merchantpage/template-9.webp'),
    publicPath('/images/merchantpage/template-10.webp'),
    publicPath('/images/merchantpage/template-2.webp'),
  ],
]
</script>

<style scoped>
@reference "@/assets/css/main.css";

.testimonial {
  @apply flex flex-col h-full gap-y-4 items-center max-lg:bg-white rounded-xl py-8 px-4;
}
</style>
